/**
 * Nice-number axis scaling for the project charts.
 *
 * Recharts only auto-fits an axis to the data when the domain is left at
 * "auto", which loses control of the tick labels. These helpers pick a
 * domain that hugs the data plus a round tick step, so a chart whose
 * points all sit in the first few percent doesn't get drawn on a 0-100%
 * canvas.
 */

export interface AxisScale {
    domain: readonly [number, number];
    ticks: ReadonlyArray<number>;
}

const MAX_TICK_INTERVALS = 5;

/** Fraction of extra room left past the largest data point. */
const HEADROOM = 1.1;

const PERCENT_STEPS: ReadonlyArray<number> = [0.01, 0.02, 0.05, 0.1, 0.25, 0.5];
const COUNT_STEPS: ReadonlyArray<number> = [1, 2, 5, 10, 20, 25, 50, 100];
const RANK_STEPS: ReadonlyArray<number> = [5, 10, 20, 25, 50];

const RANK_PADDING = 5;
const RANK_MIN = 1;
const RANK_MAX = 100;

// Steps of 0.01 accumulate binary error fast enough to push a tick past
// the domain edge, which makes Recharts drop it.
const round = (value: number): number => Math.round(value * 1e6) / 1e6;

const chooseStep = (span: number, steps: ReadonlyArray<number>): number =>
    steps.find((step) => span <= step * MAX_TICK_INTERVALS) ??
    steps[steps.length - 1];

const ticksFor = (
    min: number,
    max: number,
    step: number
): ReadonlyArray<number> => {
    if (max <= min) return [min];
    const ticks = [min];
    for (
        let tick = Math.ceil(round(min / step)) * step;
        tick < max;
        tick += step
    ) {
        const rounded = round(tick);
        if (rounded > min) ticks.push(rounded);
    }
    ticks.push(max);
    return ticks;
};

/** Scale a 0-1 ratio axis to the data, capped at 100%. */
export const percentAxisScale = (dataMax: number): AxisScale => {
    const padded = Math.min(1, Math.max(dataMax * HEADROOM, PERCENT_STEPS[0]));
    const step = chooseStep(padded, PERCENT_STEPS);
    const max = Math.min(1, round(Math.ceil(round(padded / step)) * step));
    return { domain: [0, max], ticks: ticksFor(0, max, step) };
};

/** Scale a zero-based whole-number axis to the data. */
export const countAxisScale = (dataMax: number): AxisScale => {
    const span = Math.max(dataMax, 1);
    const step = chooseStep(span, COUNT_STEPS);
    const max = Math.ceil(round(span / step)) * step;
    return { domain: [0, max], ticks: ticksFor(0, max, step) };
};

/**
 * Scale the rank-percentile axis to the plotted ranks, padded either side
 * and clamped to P1-P100. Falls back to the full range when nothing ranks.
 */
export const rankPercentileAxisScale = (
    percentiles: ReadonlyArray<number>
): AxisScale => {
    if (percentiles.length === 0) {
        return {
            domain: [RANK_MIN, RANK_MAX],
            ticks: ticksFor(RANK_MIN, RANK_MAX, 25)
        };
    }
    const low = Math.max(RANK_MIN, Math.min(...percentiles) - RANK_PADDING);
    const high = Math.min(RANK_MAX, Math.max(...percentiles) + RANK_PADDING);
    const step = chooseStep(high - low, RANK_STEPS);
    const min = Math.max(RANK_MIN, Math.floor(low / step) * step);
    const max = Math.min(RANK_MAX, Math.ceil(high / step) * step);
    return { domain: [min, max], ticks: ticksFor(min, max, step) };
};
